import { LOCALE_ID, NgModule } from "@angular/core";
import { FormBuilderComponent } from "./builder/form-builder.component";
import { FormAnswerViewerComponent } from "./answer-viewer/answer-viewer.component";
import { MatDialogModule } from "@angular/material/dialog";
import { FormOptionsComponent } from "./options/form-options.component";
import { TranslocoCoreModule } from "@i18n";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatTableModule } from "@angular/material/table";
import { MatMenuModule } from "@angular/material/menu";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { A11yModule } from "@angular/cdk/a11y";
import { FuseAlertModule, FuseConfirmationModule, FusePipesModule, FuseScrollbarModule } from "libs/fuse/src/index";
import { DragStylingService } from "./drag-styling.service";
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule, MatRippleModule } from "@angular/material/core";
import { FieldEditorComponent } from "./field-editor/field-editor.component";
import { ToolbarComponent } from "./toolbar/toolbar.component";
import { ControlPreviewComponent } from "./control-preview/control-preview.component";
import { TextBoxComponent } from "./field-types/text-box/text-box.component";
import { TextBoxEditorComponent } from "./field-types/text-box/text-box-editor.component";
import { CheckBoxComponent } from "./field-types/check-box/check-box.component";
import { CheckBoxEditorComponent } from "./field-types/check-box/check-box-editor.component";
import { TextareaComponent } from "./field-types/textarea/textarea.component";
import { TextareaEditorComponent } from "./field-types/textarea/textarea-editor.component";
import { SingleSelectionListComponent } from "./field-types/single-selection-list/single-selection-list.component";
import { SingleSelectionListEditorComponent } from "./field-types/single-selection-list/single-selection-list-editor.component";
import { MultipleSelectionListComponent } from "./field-types/multiple-selection-list/multiple-selection-list.component";
import { SingleSelectionDropdownComponent } from "./field-types/single-selection-dropdown/single-selection-dropdown.component";
import { SingleSelectionDropdownEditorComponent } from "./field-types/single-selection-dropdown/single-selection-dropdown-editor.component";
import { DatetimeComponent } from "./field-types/datetime/datetime.component";
import { DatetimeEditorComponent } from "./field-types/datetime/datetime-editor.component";
import { NumberFieldComponent } from "./field-types/number/number.component";
import { NumberFieldEditorComponent } from "./field-types/number/number-editor.component";
import { EmailFieldComponent } from "./field-types/email/email.component";
import { EmailFieldEditorComponent } from "./field-types/email/email-editor.component";
import { FormViewComponent } from "./view/form-view.component";
import { FormViewDialogComponent } from "./view/dialog/form-view-dialog.component";
import { RepeatTypeComponent } from "./repeat-section.type";
import { FormlyFieldTabs } from "./tabs.type";
import { Cid10Component } from "./field-types/cid10/cid10.component";
import { Cid10EditorComponent } from "./field-types/cid10/cid10-editor.component";
import { FormlyCid10Component } from "./field-types/cid10/formly/formly-cid10.component";
import { Cid10PickerComponent } from "./field-types/cid10/picker/cid10-picker.component";
import { FileUploaderComponent } from "./field-types/file-uploader/file-uploader.component";
import { FileUploaderEditorComponent } from "./field-types/file-uploader/file-uploader-editor.component";
import { FormlyFieldFileUploaderComponent } from "./field-types/file-uploader/formly/formly-file-uploader.component";
import { ConditionalSettingsComponent } from "./expression-settings/conditional-settings.component";
import { PredicateComponent } from "./expression-settings/predicate/predicate.component";
import { ConditionalComponent } from "./expression-settings/conditional/conditional.component";
import { TimePickerComponent } from "./field-types/time-picker/time-picker.component";
import { MatButtonToggleModule } from "@angular/material/button-toggle";
import { MatDividerModule } from "@angular/material/divider";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSidenavModule } from "@angular/material/sidenav";
import { MatSortModule } from "@angular/material/sort";
import { RouterModule } from "@angular/router";
import { defaultBuilderRoutes } from "./builder.routing";
import { MatTabsModule } from "@angular/material/tabs";
import { MatListModule } from "@angular/material/list";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatSelectModule } from "@angular/material/select";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatRadioModule } from "@angular/material/radio";
import { DragDropModule } from "@angular/cdk/drag-drop";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatToolbarModule } from "@angular/material/toolbar";
import { TranslocoModule } from "@ngneat/transloco";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";
import { NgxFileDropModule } from "ngx-file-drop";
import { FormlyMatDatepickerModule } from "@ngx-formly/material/datepicker";
import { FormlyMatToggleModule } from "@ngx-formly/material/toggle";
import { FormlyMaterialModule } from "@ngx-formly/material";
import { MatTooltipModule } from "@angular/material/tooltip";
import { FormlyModule } from "@ngx-formly/core";
import { UiNotifyModule } from "@ui/notify";
import { NgxMaterialTimepickerModule } from "ngx-material-timepicker";
import { IMaskModule } from "angular-imask";
import { EmailValidator } from "./formly/model";
import { FileUploaderType } from "./field-types/file-uploader/file-uploader";
import { TimePickerType } from "./field-types/time-picker/time-picker";
import { Cid10Type } from "./field-types/cid10/cid10";
import { CommonModule } from "@angular/common";
import { FormCollectComponent } from "./collect/form-collect.component";

export const APP_DATE_FORMATS = {
    parse: {
        dateInput: "DD/MM/YYYY",
    },
    display: {
        dateInput: "DD/MM/YYYY",
        monthYearLabel: "MMMM YYYY",
        dateA11yLabel: "LL",
        monthYearA11yLabel: "MMMM YYYY",
    },
};

@NgModule({
    declarations: [
        FieldEditorComponent,
        ToolbarComponent,
        ControlPreviewComponent,
        TextBoxComponent,
        TextBoxEditorComponent,
        CheckBoxComponent,
        CheckBoxEditorComponent,
        TextareaComponent,
        TextareaEditorComponent,
        SingleSelectionListComponent,
        SingleSelectionListEditorComponent,
        MultipleSelectionListComponent,
        SingleSelectionDropdownComponent,
        SingleSelectionDropdownEditorComponent,
        DatetimeComponent,
        DatetimeEditorComponent,
        NumberFieldComponent,
        NumberFieldEditorComponent,
        EmailFieldComponent,
        EmailFieldEditorComponent,
        FormViewComponent,
        FormViewDialogComponent,
        FormCollectComponent,
        RepeatTypeComponent,
        FormlyFieldTabs,
        Cid10Component,
        Cid10EditorComponent,
        FormlyCid10Component,
        Cid10PickerComponent,
        FileUploaderComponent,
        FileUploaderEditorComponent,
        FormlyFieldFileUploaderComponent,
        ConditionalSettingsComponent,
        PredicateComponent,
        ConditionalComponent,
        TimePickerComponent,
        FormBuilderComponent,
        FormAnswerViewerComponent,
        FormOptionsComponent,
    ],
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatDialogModule,
        TranslocoCoreModule,
        MatButtonModule,
        MatTableModule,
        A11yModule,
        FusePipesModule,
        RouterModule.forChild(defaultBuilderRoutes),
        MatButtonToggleModule,
        MatDividerModule,
        MatCheckboxModule,
        MatIconModule,
        MatProgressBarModule,
        MatProgressSpinnerModule,
        MatRippleModule,
        MatSidenavModule,
        MatSortModule,
        MatTabsModule,
        MatListModule,
        MatExpansionModule,
        MatMenuModule,
        MatSelectModule,
        MatFormFieldModule,
        MatInputModule,
        MatRadioModule,
        DragDropModule,
        MatSlideToggleModule,
        MatToolbarModule,
        TranslocoModule,
        FuseConfirmationModule,
        FuseScrollbarModule,
        MatDatepickerModule,
        MatNativeDateModule,
        NgxMatSelectSearchModule,
        NgxFileDropModule,
        FormlyMatDatepickerModule,
        FormlyMatToggleModule,
        FormlyMaterialModule,
        MatTooltipModule,
        FormlyModule.forRoot({
            validators: [{ name: "email", validation: EmailValidator }],
            types: [
                { name: "repeat", component: RepeatTypeComponent },
                { name: "tabs", component: FormlyFieldTabs },
                { name: Cid10Type.Id, component: FormlyCid10Component },
                { name: FileUploaderType.Id, component: FormlyFieldFileUploaderComponent },
                { name: TimePickerType.Id, component: TimePickerComponent },
            ],
            extras: {
                resetFieldOnHide: true,
            },
            validationMessages: [
                { name: "required", message: "Campo obrigatório" },
                { name: "email", message: "E-mail inválido" },
            ],
        }),
        UiNotifyModule,
        FuseAlertModule,
        NgxMaterialTimepickerModule.setLocale("pt-BR"),

        IMaskModule,
    ],
    providers: [
        DragStylingService,
        { provide: LOCALE_ID, useValue: "pt-BR" },
        { provide: MAT_DATE_LOCALE, useValue: "pt-BR" },
        { provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS },
    ],
    exports: [
        FormBuilderComponent,
        FormCollectComponent,
        FormViewDialogComponent,
        FormAnswerViewerComponent,
        FormOptionsComponent,
        FormViewComponent,
    ],
})
export class VFormsModule {}
